import React, { useState, useEffect } from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import PropTypes from 'prop-types';
import animateScrollTo from 'animated-scroll-to';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faYoutube } from '@fortawesome/free-brands-svg-icons';
import { faPlayCircle } from '@fortawesome/free-regular-svg-icons';
import getSlug from 'speakingurl';
import exact from 'prop-types-exact';
import Banner from '../../../common/Banner/Banner';
import Spinner from '../../../common/Spinner/Spinner';
import Sharing from '../../../common/sharing/Sharing';
import ErrorPage from '../../../common/errorPage/ErrorPage';
import Slider from '../../../common/Slider/Slider';

// basic banner config
const multiBannerConfig = {
    title: 'PUBLICIDADE',
    class: 'col-12',
    adUnit: 'a1nova-videos-multi_multi_sizes',
    sizes: [[970, 90], [728, 90], [468, 60], /* [336, 280], */[320, 50], /* [300, 250], */[300, 100], /* [250, 250], */[234, 60]],
    sizeMapping: [
        { viewport: [1250, 0], sizes: [[970, 90], [728, 90], /* [336, 280], */ /* [300, 250], */[300, 100]] },
        { viewport: [991, 0], sizes: [[728, 90], /* [336, 280], */ /* [300, 250], */[300, 100]] },
        { viewport: [365, 0], sizes: [[320, 50], /* [300, 250], */[300, 100]/* , [300, 250] */] },
        { viewport: [0, 0], sizes: [[234, 60]] },
    ],
    shouldRefresh: true,
};


const useVideoTitleParam = () => {
    const matches = useRouteMatch('/videos/:videoTitle');
    const videoTitle = matches?.params?.videoTitle;

    return videoTitle;
};

const VideosComponent = (
    { videos,
        isMobile,
        playlists,
        featuredVideo,
        notFound,
        getYoutubePlaylistsIfNeeded,
        getYoutubeVideoByTitleIfNeeded,
        getYoutubeLatestVideoIfNeeded },
) => {
    const [featuredVideoId, setFeaturedVideoId] = useState(featuredVideo?.videoId || null);
    const videoTitle = useVideoTitleParam();
    const history = useHistory();

    function _formateURL(title) {
        const formatedTitle = getSlug(title, {
            lang: 'pt',
            separator: '-',
        });

        return formatedTitle;
    }

    function _playVideo(newVideoTitle) {
        // NOTE: We are doing a new request to the youtube api everytime we click a video. This is necessary because of problems with the navigation (back navigation) and because the iframe creates an entry into the history when the src is changed (see https://stackoverflow.com/questions/821359/reload-an-iframe-without-adding-to-the-history). Also, all our attempts to make this work somehow caused problems with the google analytics logging of the page views...
        // Note, check the commits on this file if you want to see the failed attempts...

        const url = `/videos/${_formateURL(newVideoTitle)}`;

        history.push(url);
    }

    function _showVideos(playlistId) {
        // here i use playlist id to just map  correct videos of  playlist per times
        // i can do this , because api return videos object with playlist`id as key .
        const blockImageHoverStyle = { opacity: isMobile ? '1' : '0' };

        const videoItems = videos[playlistId].map((item) => {
            const img = item.videoImage;
            const _videoTitle = item.title;

            // return false when video is private
            // private vídeo not have info
            if (_videoTitle === 'Private video') { return false; }

            return (
                // @TODO properly handle the following a11y problems!
                // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
                <div onClick={_playVideo.bind(this, _videoTitle)} className="block-image" key={_videoTitle}>
                    <img src={img} alt={`Video - ${_videoTitle}`} />
                    <div style={blockImageHoverStyle} className="block-image-hover">
                        <div className="iconCover">
                            <FontAwesomeIcon icon={faPlayCircle} className="imageIcon" width="16" />
                        </div>
                        <p>
                            {_videoTitle}
                        </p>
                    </div>
                </div>
            );
        });

        // add the see more item
        videoItems.push((
            <div className="block-image" key="see More">
                <div className="seeMore">
                    <a
                        rel="noreferrer"
                        className="Button"
                        href={`https://www.youtube.com/playlist?list=${playlistId}`}
                        target="_blank"
                        title="Veja todos os vídeos"
                    >
                        Veja mais
                    </a>
                </div>
            </div>));

        return videoItems;
    }

    function _showPlaylist() {
        return playlists.map((item) => {
            const title = item.snippet.title;

            return (
                [
                    <div key={title} className="videos-playlist-content">
                        <h3>
                            {title}
                        </h3>
                        <Slider
                            autoplay={{ delay: 3000 }}
                            navigation={isMobile == null}
                            breakpoints={{
                                576: {
                                    slidesPerView: 2,
                                },
                                993: {
                                    slidesPerView: 3,
                                },
                            }}
                        >
                            {
                                // filter to remove private videos
                                _showVideos(item.id).filter((_item) => _item !== false)
                            }
                        </Slider>
                    </div>,
                    <Banner key={`${title} ad`} config={multiBannerConfig} />,
                ]
            );
        });
    }

    useEffect(() => {
        if (videoTitle) {
            // if we are on a specifics video sub page, get the video id and set the iframe (note, this function first checks the store before actually calling the api)
            getYoutubeVideoByTitleIfNeeded(videoTitle);
        } else {
            // we are on the videos home page, set the most recent video as featured video
            getYoutubeLatestVideoIfNeeded();
        }
    }, [getYoutubeLatestVideoIfNeeded, getYoutubeVideoByTitleIfNeeded, videoTitle]);


    useEffect(() => {
        if (featuredVideo.videoId !== featuredVideoId) {
            setFeaturedVideoId(null); // first setting this to false so that the iframe is removed from the page to avoid wierd history pushs (see https://stackoverflow.com/questions/821359/reload-an-iframe-without-adding-to-the-history)

            setTimeout(() => setFeaturedVideoId(featuredVideo.videoId), 50);

            animateScrollTo(0, { speed: 700 });
        }
    }, [featuredVideo, featuredVideoId]);

    useEffect(() => {
        async () => {
            await getYoutubePlaylistsIfNeeded(15);

            if (videoTitle) {
                // this comes from the url
                getYoutubeVideoByTitleIfNeeded(videoTitle);
            } else {
                getYoutubeLatestVideoIfNeeded();
            }
        };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []);

    if (notFound) {
        return <ErrorPage
            errorNumber={404}
            backLink="/videos"
            title="NÃO ACHAMOS ESSE VÍDEO :("
            message="Verifique se o link foi digitado corretamente."
            buttonText="Clique aqui para ver todos os vídeos"
        />;
    }

    const shareLocation = `/videos/${featuredVideo.slug}`;
    const titleToShare = `Estou assistindo agora: ${featuredVideo.videoTitle}`;

    return (
        <section className="videos">
            <header>
                <h1>Vídeos</h1>
                <p className="pageSubtitle">Veja nosso vídeos exclusivos</p>
            </header>
            <div className="shareBlock">
                <Sharing
                    styleEffect="pulseIcon"
                    path={shareLocation}
                    shareButtonsTo={['facebook', 'twitter', 'whatsapp']}
                    iconSize={28}
                    widthBlockIcon={40}
                    whatsapp={{ title: titleToShare }}
                    facebook={{ quote: titleToShare, hashtag: '#Antena1' }}
                    twitter={{ title: titleToShare, hashtags: ['Vídeos', 'Antena1'] }}
                />
            </div>
            <div className="videos-featured">
                {
                    featuredVideoId
                        ? <iframe
                            title={videoTitle}
                            className="featuredVideo"
                            src={`https://www.youtube.com/embed/${featuredVideoId}?autoplay=false`}
                            allowFullScreen
                        />
                        : <div>carregando...</div>
                }
                <a
                    rel="noreferrer"
                    href="https://www.youtube.com/channel/UCSkvHf0rcJFZnQUP4hT5nIQ?sub_confirmation=1"
                    className="subscribeBtn"
                    title="inscrever-se no canal antena 1"
                    target="_blank"
                >
                    <FontAwesomeIcon icon={faYoutube} width="16" />
                    <p>Inscreva-se</p>
                </a>
            </div>
            <div className="videos-playlists">
                {playlists.length > 0
                    ? _showPlaylist()
                    : <Spinner />}
            </div>
        </section>
    );
};

VideosComponent.propTypes = exact({
    getYoutubePlaylistsIfNeeded: PropTypes.func.isRequired,
    getYoutubeLatestVideoIfNeeded: PropTypes.func.isRequired,
    getYoutubeVideoByTitleIfNeeded: PropTypes.func.isRequired,
    videos: PropTypes.object,
    notFound: PropTypes.bool,
    playlists: PropTypes.array,
    isMobile: PropTypes.string,
    featuredVideo: PropTypes.object,
});

export default VideosComponent;
